// Итоги официального периода: к выдаче, выплачено, заведено, непогашено, переплачено.
//
// ## Почему пять чисел, а не одно
//
// В книге заказчика за период 15.06 — 15.07 стоит одно число: 528 842,95. Это сумма всех
// остатков вместе с отрицательными, и из-за слипания в его файле не читаются ДВЕ настоящие
// величины: 543 970,06 непогашенного у 28 человек и 15 127,11 переплат у тринадцати. Первую
// надо переносить, вторую — разбирать вручную, и действия это разные.
//
// «Заведено» стоит отдельно от «выплачено» по той же причине: заведённый транш остаток не
// уменьшает, и сложить их значило бы показать человеку в ведомости меньше, чем ему должны.
//
// Итоги считаются по ВСЕМУ периоду, а не по показанным строкам: сервер собирает их до отбора.
// Поэтому при включённом отборе полоса говорит об этом словом — иначе человек прочитает итоги
// как итоги видимого куска.

import type { OfficialTotalsOut } from '@/shared/api/endpoints/official';
import { formatKopecks } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import { Metric, MetricRow } from '@/shared/ui/metric';

export function TotalsBar({ totals }: { totals: OfficialTotalsOut }) {
  return (
    <div className='border-b border-stroke-soft-200 px-5 py-2'>
      {/* Пять величин делят полосу поровну и НЕ переносятся на вторую строку: каждая
          лишняя строка шапки — это строка сетки, которой человек не увидит.

          Ряд собран общей подписанной величиной, а не своей вёрсткой. Своя жила здесь до
          25.08.2026 и была ТРЕТЬЕЙ полосой итогов в одном разделе: у ведомости и у табеля
          полосы стояли на общем примитиве, у контура — своя, с другим цветом подписи, другим
          кеглем и своими полями.

          Крупная величина в ряду теперь ровно одна — «Всего к выдаче». Раньше крупными были
          две, и ряд переставал отвечать на вопрос, ради которого его читают. «Непогашено» и
          «Переплата» — это не второй ответ, а признак беды, и его несёт цвет, а не размер. */}
      <MetricRow columns={5}>
        <Metric
          tone='важный'
          label='Всего к выдаче'
          value={formatKopecks(totals.payable)}
          note={
            totals.writ_amount === 0
              ? 'удержаний по исполнительным документам нет'
              : `после удержания ${formatKopecks(totals.writ_amount)}`
          }
        />
        <Metric
          label='Выплачено'
          value={formatKopecks(totals.paid)}
          note='траншами с проставленной датой'
        />
        <Metric
          tone={totals.planned === 0 ? 'приглушённый' : 'обычный'}
          label='Заведено, не выдано'
          value={formatKopecks(totals.planned)}
          note={
            totals.planned === 0
              ? 'незакрытых траншей нет'
              : `${countLabel(totals.unpaid_tranches, ['транш', 'транша', 'траншей'])}; остаток эта сумма не уменьшает`
          }
        />
        <Metric
          tone={totals.unsettled_rows === 0 ? 'приглушённый' : 'тревожный'}
          label='Непогашено'
          value={formatKopecks(totals.unsettled_total)}
          note={
            totals.unsettled_rows === 0
              ? 'все строки погашены полностью'
              : totals.refused_rows > 0
                ? // Отказ от переноса — тоже непогашенный остаток, но в новый период он не
                  // уйдёт. Обещать перенос всей суммы значит разойтись с окном закрытия.
                  `${countLabel(totals.unsettled_rows, ['строка', 'строки', 'строк'])}, по ${totals.refused_rows} отказались переносить — эти деньги останутся здесь`
                : `${countLabel(totals.unsettled_rows, ['строка', 'строки', 'строк'])} — эти деньги перенесутся при закрытии`
          }
        />
        <Metric
          tone={totals.overpaid_rows === 0 ? 'приглушённый' : 'тревожный'}
          label='Переплата'
          value={formatKopecks(totals.overpaid_total)}
          note={
            totals.overpaid_rows === 0
              ? 'переплат нет'
              : `${countLabel(totals.overpaid_rows, ['строка', 'строки', 'строк'])} — выдали больше начисленного, переносом это не является`
          }
        />
      </MetricRow>
    </div>
  );
}

/**
 * Что перечислено и сколько (правило 2). Стоит над списком, а не среди денежных величин:
 * число строк — не деньги, и в одном ряду с суммами оно читается как шестая сумма.
 */
export function RowsCaption({
  totals,
  rowsShown,
  rowsTotal,
}: {
  totals: OfficialTotalsOut;
  rowsShown: number;
  rowsTotal: number;
}) {
  const отобрано = rowsShown !== rowsTotal;
  return (
    <span className='text-paragraph-xs text-text-sub-600'>
      {отобрано
        ? `Показано ${rowsShown} из ${rowsTotal}; итоги — по всему периоду, а не по отбору`
        : `${countLabel(totals.rows_count, ['строка', 'строки', 'строк'])} в периоде`}
      {!отобрано && totals.carried_in_rows > 0 && (
        <>, из них {totals.carried_in_rows} пришло переносом</>
      )}
    </span>
  );
}
